import {
  ExperienceChildConfigKind,
  ExperienceLayouts,
} from '@/types/gql.generated';
import type { Icons } from '@/ui';
import type { ThumbnailKind } from '../AddView/LayoutThumbnail';

export type LayoutFamily = 'list' | 'cards' | 'calendar';

// A customize layout renders as one of two kinds — Interactive (on-screen,
// clickable, responsive) or Printable (paper) — modeled directly with the API's
// ExperienceChildConfigKind, the same kind the server tags each creatable config with.

// The add-a-view pickers add a UI-only 'all' bucket on top of the two kinds.
export type KindFilter = ExperienceChildConfigKind | 'all';

// Kind-independent display metadata for a layout. The kind (interactive vs
// printable) is intentionally NOT part of this — the calendar-family planner
// layouts render in both kinds from the same template, so `kind` lives on the
// server config (create mode) or on a CHANGE_LAYOUT_OPTIONS row (change mode).
// Names are not here either — lib/viewName derives those from layoutId and kind.
export type LayoutRegistryEntry = {
  id: ExperienceLayouts;
  family: LayoutFamily;
  description: string;
  icon: keyof typeof Icons;
  thumbnailKind: ThumbnailKind;
};

// A layout entry resolved for the picker, with its kind attached.
export type PickerLayoutOption = LayoutRegistryEntry & {
  kind: ExperienceChildConfigKind;
};

// Display metadata for the canonical layouts shown in the Add a View / Change
// Layout picker. Labels/icons/descriptions are kind-neutral because a single
// planner layout can be offered as both interactive and printable.
export const LAYOUT_REGISTRY: Partial<
  Record<ExperienceLayouts, LayoutRegistryEntry>
> = {
  [ExperienceLayouts.PlannerMonth]: {
    id: ExperienceLayouts.PlannerMonth,
    family: 'calendar',
    description: 'A monthly calendar grid.',
    icon: 'CalendarBlank',
    thumbnailKind: 'calendar',
  },
  [ExperienceLayouts.PlannerWeek]: {
    id: ExperienceLayouts.PlannerWeek,
    family: 'calendar',
    description: 'A weekly calendar view.',
    icon: 'Calendar',
    thumbnailKind: 'weekSplit',
  },
  // The seven-column week — same week as PlannerWeek, arranged differently. Both
  // are named "Week", so this description is all that tells them apart. Offered
  // under both kinds, so it stays kind-neutral.
  [ExperienceLayouts.PrintableWeek]: {
    id: ExperienceLayouts.PrintableWeek,
    family: 'calendar',
    description: 'Seven equal day columns, side by side.',
    icon: 'Calendar',
    thumbnailKind: 'weekColumns',
  },
  [ExperienceLayouts.DayByCategory]: {
    id: ExperienceLayouts.DayByCategory,
    family: 'calendar',
    description: 'See a breakdown of each day by color, who, or label.',
    icon: 'Clock',
    thumbnailKind: 'dayColumns',
  },
  [ExperienceLayouts.ListVertical]: {
    id: ExperienceLayouts.ListVertical,
    family: 'cards',
    description: 'Clean event cards with details.',
    icon: 'CreditCard',
    thumbnailKind: 'list',
  },
  [ExperienceLayouts.ListVerticalBulleted]: {
    id: ExperienceLayouts.ListVerticalBulleted,
    family: 'list',
    description: 'Compact rows, one event per line.',
    icon: 'ListChecks',
    thumbnailKind: 'list',
  },
  // Cards family: the same ListEventCard and collection blocks, with a sticky
  // image column beside them.
  [ExperienceLayouts.SideHeaderV2]: {
    id: ExperienceLayouts.SideHeaderV2,
    family: 'cards',
    description: 'An image beside your event cards.',
    icon: 'Layout',
    thumbnailKind: 'sideBySide',
  },
  // The Cards list with a per-event image (ListEventCardWithImage). Also named
  // "Cards", so the description and thumbnail carry the difference.
  [ExperienceLayouts.ListVerticalWithImages]: {
    id: ExperienceLayouts.ListVerticalWithImages,
    family: 'cards',
    description: 'Event cards with a photo on each.',
    icon: 'Image',
    thumbnailKind: 'listImages',
  },
  // The three month-card printables. All are named "Grid", so the per-count
  // description and thumbnail carry the difference — keep the counts in both.
  [ExperienceLayouts.PrintableYearly3BoxMonthCard]: {
    id: ExperienceLayouts.PrintableYearly3BoxMonthCard,
    family: 'calendar',
    description: 'Three months side by side as cards.',
    icon: 'CreditCard',
    thumbnailKind: 'monthCards3',
  },
  [ExperienceLayouts.PrintableYearly6BoxMonthCard]: {
    id: ExperienceLayouts.PrintableYearly6BoxMonthCard,
    family: 'calendar',
    description: 'Six months per page as cards.',
    icon: 'CreditCard',
    thumbnailKind: 'monthCards6',
  },
  [ExperienceLayouts.PrintableYearlyMonthCard]: {
    id: ExperienceLayouts.PrintableYearlyMonthCard,
    family: 'calendar',
    description: 'All twelve months on one page.',
    icon: 'CreditCard',
    thumbnailKind: 'monthCards12',
  },
  [ExperienceLayouts.PrintableList]: {
    id: ExperienceLayouts.PrintableList,
    family: 'list',
    description: 'A printable list with configurable grouping.',
    icon: 'ListChecks',
    thumbnailKind: 'list',
  },
};

// ── Kinds ──────────────────────────────────────────────────────────────────────
// Single source of truth for the two customize kinds, keyed on the API's
// ExperienceChildConfigKind — Interactive renders on-screen (clickable, responsive),
// Printable renders to paper — paired here with the label/icon the pickers show.
type KindDescriptor = {
  kind: ExperienceChildConfigKind;
  label: string;
  description: string;
  icon: keyof typeof Icons;
};

// These labels stay client-side: they name the filter, not a view.
export const KINDS: KindDescriptor[] = [
  {
    kind: ExperienceChildConfigKind.Interactive,
    label: 'Interactive',
    description: 'On screen, clickable, and resizes for mobile.',
    icon: 'Layout',
  },
  {
    kind: ExperienceChildConfigKind.Printable,
    label: 'Printable',
    description: 'Designed to fit on paper. Updates automatically.',
    icon: 'Printer',
  },
];

// Canonical (layout, kind) options for the Change Layout picker. Planner layouts
// appear under both kinds; cards/list are interactive-only. Create mode does not
// use this — it builds its options from the server configs instead.
export const CHANGE_LAYOUT_OPTIONS: Array<{
  id: ExperienceLayouts;
  kind: ExperienceChildConfigKind;
}> = [
  {
    id: ExperienceLayouts.PlannerWeek,
    kind: ExperienceChildConfigKind.Interactive,
  },
  {
    id: ExperienceLayouts.PlannerMonth,
    kind: ExperienceChildConfigKind.Interactive,
  },
  // The seven-column week reads on screen as well as on paper, so it is offered
  // under both kinds — like the Week/Month grids above, from one layout.
  {
    id: ExperienceLayouts.PrintableWeek,
    kind: ExperienceChildConfigKind.Interactive,
  },
  {
    id: ExperienceLayouts.DayByCategory,
    kind: ExperienceChildConfigKind.Interactive,
  },
  {
    id: ExperienceLayouts.ListVertical,
    kind: ExperienceChildConfigKind.Interactive,
  },
  {
    id: ExperienceLayouts.ListVerticalBulleted,
    kind: ExperienceChildConfigKind.Interactive,
  },
  {
    id: ExperienceLayouts.SideHeaderV2,
    kind: ExperienceChildConfigKind.Interactive,
  },
  {
    id: ExperienceLayouts.ListVerticalWithImages,
    kind: ExperienceChildConfigKind.Interactive,
  },
  {
    id: ExperienceLayouts.PlannerWeek,
    kind: ExperienceChildConfigKind.Printable,
  },
  {
    id: ExperienceLayouts.PlannerMonth,
    kind: ExperienceChildConfigKind.Printable,
  },
  {
    id: ExperienceLayouts.PrintableWeek,
    kind: ExperienceChildConfigKind.Printable,
  },
  {
    id: ExperienceLayouts.PrintableYearly3BoxMonthCard,
    kind: ExperienceChildConfigKind.Printable,
  },
  {
    id: ExperienceLayouts.PrintableYearly6BoxMonthCard,
    kind: ExperienceChildConfigKind.Printable,
  },
  {
    id: ExperienceLayouts.PrintableYearlyMonthCard,
    kind: ExperienceChildConfigKind.Printable,
  },
  {
    id: ExperienceLayouts.PrintableList,
    kind: ExperienceChildConfigKind.Printable,
  },
];

/** Canonical change-layout options, optionally filtered by kind. */
export const getPickerLayouts = (
  kind?: ExperienceChildConfigKind
): PickerLayoutOption[] =>
  CHANGE_LAYOUT_OPTIONS.filter((o) => !kind || o.kind === kind).flatMap((o) => {
    const entry = LAYOUT_REGISTRY[o.id];
    return entry ? [{ ...entry, kind: o.kind }] : [];
  });

// ── Legacy display fallbacks ───────────────────────────────────────────────────
//
// Labels and icons for layoutIds that existing children may carry but are no longer
// in the canonical picker. Used by ViewSelector and ViewPickerDropdown so children
// created before this registry was introduced still display a sensible name/icon.

export const LEGACY_LAYOUT_ICONS: Partial<
  Record<ExperienceLayouts, keyof typeof Icons>
> = {
  [ExperienceLayouts.Grid]: 'Layout',
  [ExperienceLayouts.ListTable]: 'Layout',
  [ExperienceLayouts.ListVerticalBulletedCondensed]: 'ListChecks',
  [ExperienceLayouts.PlannerCalendar]: 'CalendarBlank',
  [ExperienceLayouts.PlannerMonth]: 'CalendarBlank',
  [ExperienceLayouts.PrintVertical]: 'Printer',
  [ExperienceLayouts.PrintableCalendar]: 'Printer',
  [ExperienceLayouts.PrintableListVertical]: 'Printer',
  [ExperienceLayouts.PrintableYearly3BoxMonthCard]: 'Printer',
  [ExperienceLayouts.PrintableYearly6BoxMonthCard]: 'Printer',
  [ExperienceLayouts.PrintableYearlyMonthCard]: 'Printer',
  [ExperienceLayouts.SideHeader]: 'Layout',
  [ExperienceLayouts.SingleImage]: 'Image',
};

/** Icon for any layoutId — canonical registry first, then legacy fallback. */
export const layoutIcon = (id: ExperienceLayouts): keyof typeof Icons =>
  LAYOUT_REGISTRY[id]?.icon ?? LEGACY_LAYOUT_ICONS[id] ?? 'Layout';

/**
 * Icon for a child view in the view pickers. Printable children always show the
 * printer icon regardless of the layout they render, so the paper output reads at
 * a glance without a badge; interactive children keep their layout's icon.
 */
export const viewIcon = (
  id: ExperienceLayouts,
  isPrintable: boolean
): keyof typeof Icons => (isPrintable ? 'Printer' : layoutIcon(id));

/** ThumbnailKind for any layoutId — canonical registry first, then 'list' fallback. */
export const layoutThumbnailKind = (id: ExperienceLayouts): ThumbnailKind =>
  LAYOUT_REGISTRY[id]?.thumbnailKind ?? 'list';
